/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Card, Col, Form, Modal, Row, TimePicker } from 'antd';
import { useWorkorderDetailContext } from '../WorkorderDetailProvider';
import { ComplementOriginEnum } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { FloatingFormItem, Group, InputDate } from '@/components/_common';
import moment, { Moment } from 'moment';
import { useWorkOrderComplementEditSchedule } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { useCallback, useEffect, useState } from 'react';
import { useDefaultEmptyFieldError } from '@/hooks';
import {
  WorkOrderComplement,
  WorkOrderComplementStatus,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useNotification } from '@/contexts/WebsocketProvider/Notification.provider';
import { UserNotificationOriginEnum } from '@/core/domain/communication/userNotification/userNotification.types';
import { APP_NAME } from '@/index';

interface FormValues {
  scheduleDateTime?: Moment;
  scheduleEndDateTime?: Moment;
  receptionDateTime?: Moment;
}

export default function WorkOrderInfoSchedule() {
  const {
    workorder: { data: workorder },
    complement: { data: complement, refetch },
  } = useWorkorderDetailContext();

  const { userNotification } = useNotification();

  const [disabled, setDisabled] = useState(false);

  const isOpenedOrAuthorized =
    complement?.status === WorkOrderComplementStatus.Open ||
    complement?.status === WorkOrderComplementStatus.Authorized;

  const [form] = Form.useForm<FormValues>();

  const fieldRequired = useDefaultEmptyFieldError();

  const scheduleDateTime = Form.useWatch('scheduleDateTime', form);
  const scheduleEndDateTime = Form.useWatch('scheduleEndDateTime', form);

  useEffect(() => {
    if (
      !userNotification ||
      ![
        UserNotificationOriginEnum.WORKORDER,
        UserNotificationOriginEnum.WORKORDER_MYBCS,
      ].includes(userNotification.origin) ||
      typeof userNotification?.metadata !== 'string' ||
      !userNotification.metadata ||
      !complement?.id ||
      !workorder?.id
    )
      return;

    const metadata: { workOrderId?: number; complementId?: number } =
      JSON.parse(userNotification.metadata);

    if (
      metadata?.complementId === complement?.id &&
      metadata?.workOrderId === workorder?.id &&
      disabled
    ) {
      (async () => {
        const result = await refetch();
        formUpdate(result.data);
        setDisabled(false);
      })();
    }
  }, [userNotification, workorder?.id, complement?.id]);

  useEffect(() => {
    formUpdate(complement);
  }, [complement]);

  const formUpdate = (complement?: WorkOrderComplement) => {
    form.setFieldsValue({
      scheduleDateTime: complement?.scheduleDateTime
        ? moment(complement?.scheduleDateTime)
        : undefined,
      scheduleEndDateTime: complement?.scheduleEndDateTime
        ? moment(complement?.scheduleEndDateTime)
        : undefined,
      receptionDateTime: complement?.receptionDateTime
        ? moment(complement?.receptionDateTime)
        : undefined,
    });
  };

  const editSchedule = useWorkOrderComplementEditSchedule();

  const isLoading = editSchedule.isLoading;

  const handleUpdate = async (values: FormValues) => {
    try {
      await form.validateFields();

      if (
        !workorder ||
        !complement ||
        !values.scheduleDateTime ||
        !values.scheduleEndDateTime
      )
        return;

      const dateTime = 'YYYY-MM-DDTHH:mm:ss';

      await editSchedule.mutateAsync({
        complementId: complement?.id,
        workOrderId: workorder?.id,
        scheduleDateTime: values.scheduleDateTime?.format(dateTime),
        scheduleEndDateTime: values.scheduleEndDateTime?.format(dateTime),
        receptionDateTime: values.receptionDateTime?.format(dateTime),
      });

      setDisabled(true);

      Modal.confirm({
        icon: null,
        title: 'Atualização de Agendamento – Bosch',
        width: 500,
        okButtonProps: { style: { display: 'none' } },
        cancelText: 'Confirmar',
        content: (
          <>
            <p>
              Solicitamos a atualização das datas do agendamento junto à{' '}
              <b>Bosch</b>. Assim que a plataforma deles responder, você
              receberá uma notificação informando se o agendamento foi ou não
              atualizado.
            </p>
            <p>
              <b>Por que a atualização pode ser negada?</b>
              <br />
              Como o agendamento é gerenciado diretamente pela plataforma da{' '}
              <b>Bosch</b>, a plataforma <b>{APP_NAME}</b> precisa consultar a
              integração para verificar se há um novo dia/horário disponível com
              base nas informações enviadas. Se houver disponibilidade, o
              agendamento será atualizado e você será notificado. Caso não haja,
              você também será avisado.
            </p>
          </>
        ),
      });
    } catch {}
  };

  const disableStartDate = useCallback(
    (date?: Moment) => {
      const currentStartDate = complement?.scheduleDateTime
        ? moment(complement.scheduleDateTime)
        : undefined;

      return currentStartDate ? !!date?.isBefore(currentStartDate) : true;
    },
    [complement?.scheduleDateTime, scheduleEndDateTime],
  );

  const disabledStartTime = (date?: Moment) => {
    const currentStartDate = complement?.scheduleDateTime
      ? moment(complement.scheduleDateTime)
      : undefined;

    const startHours = currentStartDate?.hours() || 0;
    const startRangeHours = Array.from({ length: startHours }).map(
      (_, index) => index,
    );
    const startMinutes = currentStartDate?.minutes() || 0;
    const startRangeMinutes = Array.from({ length: startMinutes }).map(
      (_, index) => index,
    );

    const endHours = scheduleEndDateTime?.hours() || 0;
    const endRangeHours = Array.from({ length: 24 - endHours }).map(
      (_, index) => index + 1 + endHours,
    );
    const endMinutes = scheduleEndDateTime?.minutes() || 0;
    const endRangeMinutes = Array.from({ length: 60 - endMinutes }).map(
      (_, index) => index + 1 + endMinutes,
    );

    return {
      disabledHours: () => {
        if (
          date?.isSame(currentStartDate, 'day') &&
          date?.isSame(scheduleEndDateTime, 'day')
        ) {
          return [...startRangeHours, ...endRangeHours];
        } else if (date?.isSame(currentStartDate, 'day')) {
          return startRangeHours;
        } else if (date?.isSame(scheduleEndDateTime, 'day')) {
          return endRangeHours;
        }
        return [];
      },
      disabledMinutes: (hours: number) => {
        if (hours === startHours && hours === endHours) {
          return [...startRangeMinutes, ...endRangeMinutes];
        } else if (hours === startHours) {
          return startRangeMinutes;
        } else if (hours === endHours) {
          return endRangeMinutes;
        }
        return [];
      },
    };
  };

  const disableEndDate = (date?: Moment) => {
    const startDate = complement?.scheduleDateTime
      ? moment(complement.scheduleDateTime)
      : scheduleDateTime?.clone();

    return !!date?.isBefore(startDate);
  };

  const disabledEndTime = (date?: Moment) => {
    const startHours = scheduleDateTime?.hours() || 0;
    const startMinutes = scheduleDateTime?.minutes() || 0;

    return {
      disabledHours: () =>
        date?.isSame(scheduleDateTime, 'day')
          ? Array.from({ length: startHours }).map((_, index) => index)
          : [],
      disabledMinutes: (hours: number) => {
        if (hours === startHours) {
          return Array.from({ length: startMinutes }).map((_, index) => index);
        }
        return [];
      },
    };
  };

  const disableTimes = useCallback(() => {
    const startHour = scheduleDateTime?.clone()?.hours() || 0;
    const startMinute = scheduleDateTime?.clone()?.minutes() || 0;

    const rangeHours = Array.from({ length: 24 - startHour }).map(
      (_, index) => index + startHour + 1,
    );

    const rangeStartMinutes = Array.from({ length: 60 - startMinute }).map(
      (_, index) => index + startMinute + 1,
    );

    return {
      disabledHours: () => rangeHours,
      disabledMinutes: (hours: number) => {
        if (hours === startHour) {
          return rangeStartMinutes;
        }
        return [];
      },
    };
  }, [scheduleDateTime, scheduleEndDateTime]);

  if (complement?.origin !== ComplementOriginEnum.MyBoschCarService)
    return null;

  return (
    <Form form={form} onFinish={handleUpdate}>
      <Card
        style={{ borderRadius: 10, marginTop: 16 }}
        title="Datas de agendamento"
      >
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <FloatingFormItem
              label="Inicio do atendimento agendado"
              name="scheduleDateTime"
              getValueFromEvent={(value) => {
                form.validateFields([
                  'scheduleEndDateTime',
                  'receptionDateTime',
                ]);

                const receptionDateTime = form.getFieldValue(
                  'receptionDateTime',
                ) as Moment;

                if (receptionDateTime && value) {
                  form.setFieldValue(
                    'receptionDateTime',
                    moment(value)
                      .hours(receptionDateTime?.hours())
                      .minutes(receptionDateTime?.minutes()),
                  );
                }

                return value;
              }}
              rules={[
                fieldRequired,
                {
                  validator: (_, value: Moment) => {
                    const endDate = scheduleEndDateTime?.clone();

                    if (!value || !endDate) return Promise.resolve();

                    if (value.isAfter(endDate)) {
                      return Promise.reject('Início maior que o termíno');
                    }

                    if (value.isBefore(endDate.startOf('day'))) {
                      return Promise.reject(`Dia diferente do termíno`);
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputDate
                showTime
                disabledDate={disableStartDate}
                disabledTime={disabledStartTime as any}
                allowClear
                disabled={isLoading || disabled || !isOpenedOrAuthorized}
              />
            </FloatingFormItem>
          </Col>

          <Col span={8}>
            <FloatingFormItem
              label="Término do atendimento agendado"
              name="scheduleEndDateTime"
              getValueFromEvent={(value) => {
                form.validateFields(['scheduleDateTime', 'receptionDateTime']);
                return value;
              }}
              rules={[
                fieldRequired,
                {
                  validator: (_, value: Moment) => {
                    const startDate = scheduleDateTime?.clone();

                    if (!value || !startDate) return Promise.resolve();

                    if (value.isBefore(startDate)) {
                      return Promise.reject('Término menor que o ínicio');
                    }

                    if (value.isAfter(startDate.endOf('day'))) {
                      return Promise.reject(`Dia direfente do início`);
                    }

                    return Promise.resolve(null);
                  },
                },
              ]}
            >
              <InputDate
                showTime
                disabledDate={disableEndDate}
                disabledTime={disabledEndTime as any}
                allowClear
                disabled={isLoading || disabled || !isOpenedOrAuthorized}
              />
            </FloatingFormItem>
          </Col>

          <Col span={8}>
            <FloatingFormItem
              key={scheduleDateTime?.toString()}
              label="Horário de recepção do veículo"
              name="receptionDateTime"
              getValueFromEvent={(value?: Moment) => {
                if (!value) return value;

                if (scheduleDateTime) {
                  return scheduleDateTime.clone().set({
                    hour: value.hour(),
                    minute: value.minute(),
                    second: 0,
                    millisecond: 0,
                  });
                }

                return value;
              }}
              rules={[
                {
                  validator: (_, value: Moment) => {
                    if (!value) return Promise.resolve(null);

                    const startDate = scheduleDateTime?.clone();

                    const isAfterHours = value.isAfter(startDate);
                    // const isSame = value.isSame(
                    //   startDate,
                    //   'hours',
                    // );
                    // const isAfterMinutes = value.isAfter(
                    //   startDate,
                    //   'minutes',
                    // );

                    if (isAfterHours) {
                      return Promise.reject(
                        'Horário deve ser menor ou igual ao início',
                      );
                    }

                    return Promise.resolve(null);
                  },
                },
              ]}
            >
              <TimePicker
                disabledTime={disableTimes}
                style={{ width: '100%' }}
                allowClear
                minuteStep={10}
                showSecond={false}
                format="HH:mm"
                disabled={isLoading || disabled || !isOpenedOrAuthorized}
                placeholder=""
              />
            </FloatingFormItem>
          </Col>

          {!disabled && isOpenedOrAuthorized && (
            <Col span={24}>
              <Group position="right">
                <Button type="primary" htmlType="submit" loading={isLoading}>
                  Salvar dados
                </Button>
              </Group>
            </Col>
          )}
        </Row>
      </Card>
    </Form>
  );
}
